// NoteFormFields.tsx - Reusable form fields component

import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import Col from '../Col';
import Input from '../Input';
import Picker from '../Picker';
import Row from '../Row';
import Toggle from '../Toggle';
import Toast from '../Toast/Toast';
import CheckBox from '../Checkbox';
import File from '../File';
import TextEditor from '../Editor/Editor';
import Overlay from '../../components/Overlay';
import Marker from '../AnnotateImage/Marker';
import { onCheckDocument } from 'src/helpers/CommonHelpers';
import { selectUserProfileDetails } from 'src/store/selectors/settings';
import { variables } from 'src/Tenants';
import { toast } from 'react-toastify';
import {
  IRawNoteFormData,
  NoteAppointment,
} from 'src/SharedCustomHooks/UseNotesAPI/types';

interface NoteFormFieldsProps {
  formData: IRawNoteFormData;
  editorState: string;
  breadcrumb?: string;
  from: string;
  onFormDataChange: (updates: Partial<IRawNoteFormData>) => void;
  onEditorChange: (newEditorState: string) => void;
  onGetUserProfileDetails: (userId: string) => void;
  userId: string;
}

// Type guard to check if appointment is an object
const isAppointmentObject = (
  appointment: any
): appointment is NoteAppointment => {
  return (
    appointment &&
    typeof appointment === 'object' &&
    'date' in appointment &&
    'time' in appointment
  );
};

const NoteFormFields: React.FC<NoteFormFieldsProps> = ({
  formData,
  editorState,
  breadcrumb,
  from,
  onFormDataChange,
  onEditorChange,
  onGetUserProfileDetails,
  userId,
}) => {
  const settings = useSelector(selectUserProfileDetails);
  const [file, setFile] = useState(null);
  const [showOverlay, setShowOverlay] = useState(false);

  const handleRemainderToggle = (value: any) => {
    const noteNotification = settings?.noteNotification;

    if (settings && noteNotification) {
      const enableReminder = (
        hasNotification: boolean,
        contextName: string
      ) => {
        if (hasNotification) {
          onFormDataChange({
            appointment: value
              ? null
              : ({ date: null, time: null } as NoteAppointment),
          });
          return true;
        } else {
          toast.success(
            `Please enable ${contextName} Note Email Notification to add reminder`,
            { autoClose: 10000, position: 'top-center' }
          );
          return false;
        }
      };

      switch (from) {
        case 'properties':
          enableReminder(noteNotification.properties, 'Properties');
          break;
        case 'suites':
          enableReminder(noteNotification.suites, variables.suiteName);
          break;
        case 'serviceRequests':
          enableReminder(
            noteNotification.serviceRequests,
            variables.serviceRequest
          );
          break;
        case 'assets':
          enableReminder(noteNotification.assets, 'Assets');
          break;
        case 'inventoryItems':
          enableReminder(noteNotification.inventoryItems, 'InventoryItem');
          break;
        case 'contacts':
          enableReminder(noteNotification.contacts, 'Contacts');
          break;
        default:
          break;
      }
    } else {
      onGetUserProfileDetails(userId);
      alert('Loading Please wait!');
    }
  };

  const handleAppointmentDateChange = (date: any) => {
    if (isAppointmentObject(formData.appointment)) {
      const currentAppointment = formData.appointment as NoteAppointment;
      onFormDataChange({
        appointment: {
          ...currentAppointment,
          date: date,
        },
      });
    }
  };

  const handleAppointmentTimeChange = (time: any) => {
    if (isAppointmentObject(formData.appointment)) {
      const currentAppointment = formData.appointment as NoteAppointment;
      onFormDataChange({
        appointment: {
          ...currentAppointment,
          time: time,
        },
      });
    }
  };

  return (
    <>
      <Row mdCenter>
        <Col xs="12" md="6">
          {breadcrumb && (
            <>
              <Toast
                message={
                  <>
                    You are adding a note to <strong>{breadcrumb}</strong>.
                  </>
                }
                actions={undefined}
                color={undefined}
              />
              <br />
              <br />
            </>
          )}

          <Input
            value={formData.title}
            onChange={(e: any) => onFormDataChange({ title: e.target.value })}
            label="Subject"
            required
            placeholder="Subject"
            block
          />

          <div className="input">
            <div>
              <CheckBox
                value={formData.noteVisibleToHomeownerOccupantRealtor}
                checked={formData.noteVisibleToHomeownerOccupantRealtor}
                label="Display note for homeowners, occupants, realtors"
                onChange={(e: any) =>
                  onFormDataChange({
                    noteVisibleToHomeownerOccupantRealtor: e.target.checked,
                  })
                }
              />
            </div>
          </div>

          <div className="input">
            <div className="input__require">
              <label className="input__label">Description</label>
              <div className="input__required" />
            </div>
            <div
              style={{
                borderBottom: '0.1rem solid rgba(83, 83, 83, 0.25)',
              }}
              className="input__control"
            >
              <TextEditor
                editorState={editorState}
                onEditorStateChange={onEditorChange}
              />
            </div>
          </div>

          <File
            label="Image"
            onSelect={(file: any) => {
              onCheckDocument(file) && onFormDataChange({ image: file });
            }}
            files={
              Array.isArray(formData.image) && formData.image.length === 1
                ? formData.image
                : []
            }
            image={
              Array.isArray(formData.image) && formData.image.length > 1
                ? formData.image
                : null
            }
            errors={''}
            type="image"
          />

          <Toggle
            label="Set a Reminder"
            value={!!formData.appointment}
            onToggle={(value: any) => handleRemainderToggle(value)}
          />
          {isAppointmentObject(formData.appointment) && (
            <>
              <Picker
                showPopperArrow={false}
                placeholderText="Date"
                selected={
                  formData.appointment.date instanceof Date &&
                  !isNaN(formData.appointment.date.getTime())
                    ? formData.appointment.date
                    : null
                }
                onChange={handleAppointmentDateChange}
                label={undefined}
                required={undefined}
              />
              <Picker
                label="Include a Time"
                placeholderText="Time"
                showPopperArrow={false}
                selected={
                  formData.appointment.time instanceof Date &&
                  !isNaN(formData.appointment.time.getTime())
                    ? formData.appointment.time
                    : null
                }
                onChange={handleAppointmentTimeChange}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={15}
                timeCaption="Time"
                dateFormat="h:mm aa"
                required={undefined}
              />
            </>
          )}
        </Col>
      </Row>

      <Overlay
        showOverlay={showOverlay}
        content={<Marker onClose={() => setShowOverlay(false)} img={file} />}
      />
    </>
  );
};

export default NoteFormFields;
